import { useState, useEffect, useCallback } from 'react';
import { PageConfig, ReferralData } from '../types';

interface UseAppDataReturn {
  config: PageConfig | null;
  allData: Record<string, ReferralData[]>;
  currentTabData: ReferralData[];
  loading: boolean;
  error: string | null;
  updateTabData: (dataKey: string, updater: (data: ReferralData[]) => ReferralData[]) => void;
  updateRecordInAllTabs: (id: string, updater: (record: ReferralData) => ReferralData) => void;
}

export const useAppData = (tabValue: number): UseAppDataReturn => {
  const [config, setConfig] = useState<PageConfig | null>(null);
  const [allData, setAllData] = useState<Record<string, ReferralData[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const configData: any = {};
        setConfig((configData?.pageConfig as PageConfig) ?? null);
        setAllData((configData?.mockData as Record<string, ReferralData[]>) ?? {});
      } catch (err) {
        console.error('Failed to load configuration:', err);
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const currentTabData = config && allData ? allData[config.tabs[tabValue]?.dataKey] || [] : [];

  const updateTabData = useCallback(
    (dataKey: string, updater: (data: ReferralData[]) => ReferralData[]) => {
      setAllData((prev) => ({
        ...prev,
        [dataKey]: updater(prev[dataKey] || []),
      }));
    },
    []
  );

  const updateRecordInAllTabs = useCallback(
    (id: string, updater: (record: ReferralData) => ReferralData) => {
      setAllData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          updated[key] = updated[key].map((item) => (item.id === id ? updater(item) : item));
        });
        return updated;
      });
    },
    []
  );

  return {
    config,
    allData,
    currentTabData,
    loading,
    error,
    updateTabData,
    updateRecordInAllTabs,
  };
};
